import { db } from '../firebase-config';
import { collection, getDocs, doc, setDoc, getDoc, updateDoc, query, where, orderBy, Timestamp } from 'firebase/firestore';

/**
 * Génère les feuilles de temps mensuelles pour tous les employés
 * À appeler manuellement par l'admin en fin de mois
 */
export async function generateMonthlyTimesheets(targetMonth = null) {
  console.log('📅 Début de la génération des feuilles mensuelles');
  
  try {
    // TEMPORAIRE : Générer pour les 4 derniers mois
    const now = new Date();
    const monthsToGenerate = [];
    
    // Générer pour les 4 derniers mois (mois -1, -2, -3, -4)
    for (let i = 1; i <= 4; i++) {
      monthsToGenerate.push(new Date(now.getFullYear(), now.getMonth() - i, 1));
    }
    
    console.log(`📅 Génération pour ${monthsToGenerate.length} mois`);
    
    const allResults = {
      success: [],
      skipped: [],
      errors: []
    };
    
    // Générer pour chaque mois
    for (const lastMonth of monthsToGenerate) {
      const monthResult = await generateSingleMonth(lastMonth);
      
      // Fusionner les résultats
      allResults.success.push(...monthResult.success);
      allResults.skipped.push(...monthResult.skipped);
      allResults.errors.push(...monthResult.errors);
    }
    
    console.log('🎉 Génération terminée !');
    console.log(`✅ ${allResults.success.length} feuille(s) créée(s)`);
    console.log(`⚠️ ${allResults.skipped.length} feuille(s) ignorée(s)`);
    console.log(`❌ ${allResults.errors.length} erreur(s)`);
    
    return {
      success: true,
      monthName: 'Plusieurs mois',
      year: now.getFullYear(),
      results: allResults
    };
    
  } catch (error) {
    console.error('❌ Erreur lors de la génération:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Génère les feuilles pour un mois donné
 */
async function generateSingleMonth(lastMonth) {
  try {
    // Calculer le 1er et dernier jour du mois (correction fuseau horaire)
    const year = lastMonth.getFullYear();
    const month = lastMonth.getMonth();
    
    const monthStart = new Date(year, month, 1, 0, 0, 0, 0);
    const monthEnd = new Date(year, month + 1, 0, 23, 59, 59, 999);
    
    const monthName = getMonthName(lastMonth);
    
    console.log(`📅 Génération pour : ${monthName} ${year}`);
    console.log(`📅 Période: ${monthStart.toISOString().slice(0,10)} au ${monthEnd.toISOString().slice(0,10)}`);
    
    // Récupérer tous les employés
    const employeesSnap = await getDocs(collection(db, 'employees'));
    const employees = employeesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    console.log(`👥 ${employees.length} employé(s) trouvé(s)`);
    
    const results = {
      success: [],
      skipped: [],
      errors: []
    };
    
    for (const emp of employees) {
      try {
        const monthId = `${emp.email}_${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, '0')}`;
        const monthRef = doc(db, 'workSessionsMonth', monthId);
        
        // Vérifier si existe déjà
        const monthDoc = await getDoc(monthRef);
        if (monthDoc.exists()) {
          console.log(`⚠️ Feuille déjà existante pour ${emp.email}`);
          results.skipped.push(emp.email);
          continue;
        }
        
        // Récupérer les sessions du mois
        const sessionsQuery = query(
          collection(db, 'workSessions'),
          where('employeeEmail', '==', emp.email),
          where('startTime', '>=', Timestamp.fromDate(monthStart)),
          where('startTime', '<=', Timestamp.fromDate(monthEnd)),
          orderBy('startTime', 'asc')
        );
        
        const sessionsSnap = await getDocs(sessionsQuery);
        
        const sessions = sessionsSnap.docs
          .map(doc => {
            const data = doc.data();
            
            // Ignorer les sessions sans startTime ou endTime
            if (!data.startTime || !data.endTime) {
              console.warn(`⚠️ Session ${doc.id} ignorée (manque startTime ou endTime)`);
              return null;
            }
            
            const session = {
              id: doc.id,
              startTime: data.startTime,
              endTime: data.endTime,
              startLocation: data.startLocation || null,
              endLocation: data.endLocation || null,
              totalSessionTime: data.totalSessionTime || 0,
              totalPauseTime: data.totalPauseTime || 0,
              totalWorkTime: data.totalWorkTime || 0,
              status: data.status || 'Validé',
              siteDeTravail: data.siteDeTravail || null,
              breaks: data.breaks || []
            };
            
            // Ajouter device seulement s'il existe
            if (data.device) {
              session.device = data.device;
            }
            
            return session;
          })
          .filter(session => session !== null); // Supprimer les nulls
        
        console.log(`📊 ${sessions.length} session(s) pour ${emp.email}`);
        
        if (sessions.length === 0) {
          console.log(`⏭️ Pas de sessions pour ${emp.email}, on passe`);
          results.skipped.push(emp.email);
          continue;
        }
        
        // Calculer les totaux
        let totalWorkTime = 0;
        let totalPauseTime = 0;
        let totalSessionTime = 0;
        
        sessions.forEach(session => {
          totalWorkTime += session.totalWorkTime || 0;
          totalPauseTime += session.totalPauseTime || 0;
          totalSessionTime += session.totalSessionTime || 0;
        });
        
        // Récupérer les absences validées du mois
        const absencesQuery = query(
          collection(db, 'absences'),
          where('employeeEmail', '==', emp.email),
          where('status', '==', 'Validé'),
          where('startDate', '<=', monthEnd.toISOString().slice(0, 10)),
          where('endDate', '>=', monthStart.toISOString().slice(0, 10))
        );
        
        const absencesSnap = await getDocs(absencesQuery);
        
        const absences = absencesSnap.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            type: data.type,
            startDate: data.startDate,
            endDate: data.endDate,
            startPart: data.startPart,
            endPart: data.endPart,
            status: data.status
          };
        });
        
        // Créer la feuille mensuelle
        const documentData = {
          employeeId: emp.id || null,
          employeeEmail: emp.email,
          employeeName: `${emp.prenom || ''} ${emp.nom || ''}`.trim() || emp.email,
          monthStart: `${year}-${String(month + 1).padStart(2, '0')}-01`,
          monthEnd: `${year}-${String(month + 1).padStart(2, '0')}-${new Date(year, month + 1, 0).getDate()}`,
          monthName: monthName,
          year: year,
          sessions,
          absences,
          totalWorkTime: totalWorkTime || 0,
          totalPauseTime: totalPauseTime || 0,
          totalSessionTime: totalSessionTime || 0,
          sessionCount: sessions.length,
          absenceCount: absences.length,
          status: 'En attente de validation Admin',
          debloque: false,
          signed: false,
          adminValidatedAt: null,
          employeeSignedAt: null,
          pdfUrl: null,
          createdAt: Timestamp.fromDate(new Date()),
          updatedAt: Timestamp.fromDate(new Date())
        };
        
        // Log pour détecter les undefined
        console.log('🔍 DEBUG - Document data pour', emp.email);
        Object.keys(documentData).forEach(key => {
          if (documentData[key] === undefined) {
            console.error(`❌ Champ undefined trouvé: ${key}`);
          }
        });
        
        // Vérifier les sessions
        sessions.forEach((session, idx) => {
          Object.keys(session).forEach(key => {
            if (session[key] === undefined) {
              console.error(`❌ Session ${idx} - Champ undefined: ${key}`);
            }
          });
        });
        
        // Vérifier les absences
        absences.forEach((absence, idx) => {
          Object.keys(absence).forEach(key => {
            if (absence[key] === undefined) {
              console.error(`❌ Absence ${idx} - Champ undefined: ${key}`);
            }
          });
        });
        
        await setDoc(monthRef, documentData);
        
        const heures = Math.round(totalWorkTime / 3600000);
        const minutes = Math.round((totalWorkTime % 3600000) / 60000);
        
        console.log(`✅ Feuille créée pour ${emp.email} - ${heures}h${minutes}min`);
        results.success.push({ email: emp.email, hours: `${heures}h${minutes}min` });
        
      } catch (error) {
        console.error(`❌ Erreur pour ${emp.email}:`, error);
        results.errors.push({ email: emp.email, error: error.message });
      }
    }
    
    return results;
    
  } catch (error) {
    console.error('❌ Erreur lors de la génération du mois:', error);
    return {
      success: [],
      skipped: [],
      errors: [{ email: 'tous', error: error.message }]
    };
  }
}

/**
 * Obtenir le nom du mois en français
 */
function getMonthName(date) {
  const months = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];
  return months[date.getMonth()];
}

/**
 * Valider une feuille de temps (admin)
 */
export async function validateMonthlyTimesheet(timesheetId) {
  try {
    const timesheetRef = doc(db, 'workSessionsMonth', timesheetId);
    
    await updateDoc(timesheetRef, {
      status: 'En attente de signature salarié',
      adminValidatedAt: Timestamp.fromDate(new Date()),
      updatedAt: Timestamp.fromDate(new Date())
    });
    
    console.log('✅ Feuille validée par l\'admin');
    return { success: true };
    
  } catch (error) {
    console.error('❌ Erreur validation:', error);
    return { success: false, error: error.message };
  }
}